// S31 First run (Tool Plan §4.14; D-80, D-77; I-1a Batch 4, IP2-29).
//
// One calm path from the first open to Home, one idea per screen, Back on each.
// Nothing is recorded before it ends. Steps:
//   1 install guard (browser tab only) — "just look around" keeps nothing
//   2 language line (follows the device; English only until I-1b)
//   3 what it is + not an emergency service + the 18+ tap (the only required tap)
//   4 privacy in plain words
//   5 choose a lock (honest 6-digit-PIN limit at the choice)
//   6 optional recovery code + the backup offer (S33 = Batch 6)
// It ends by writing the keyring (add-only). If another copy finished first run
// meanwhile, its key wins and this copy goes to Unlock.
// Help now + Hide join the top bar in Batch 5.
//
// Restore from a backup file (I-1a Batch 6b; Implementation Plan v02 §6.4,
// IP2-36 (c)): a quiet entry on step 4, so it is reached only after the 18+ tap
// and only in the installed app (a browser tab stops at the install guard).
//   R1 pick the file + its backup passphrase → checked and opened, nothing written
//   R2 choose a lock for this phone (same rules as step 5)
//   then step 6 (optional recovery code) → the restore writes the new keyring
//   and every record in one go, into an empty diary only.

import { type FormEvent, useEffect, useRef, useState } from 'react'

import type { OpenedBackup } from '../export'
import { type EncryptedStore, type LockSession, newRecoveryCode } from '../lock'
import { isInstalledApp } from '../shell/displayMode'
import { nextFrame } from '../ui/nextFrame'
import { ScreenTitle } from '../ui/ScreenTitle'
import { SecretField } from '../ui/SecretField'

import { common, installGuard, intendedUse, language, privacy, recovery, restore, setLock } from './copy'
import { SetLock } from './SetLock'

type Step =
  | 'install'
  | 'look-around'
  | 'language'
  | 'intended-use'
  | 'privacy'
  | 'lock'
  | 'recovery'
  | 'restore-file'
  | 'restore-lock'

interface FirstRunProps {
  readonly session: LockSession
  readonly onOpened: (store: EncryptedStore) => void
  readonly onAlreadySetUp: () => void
}

export function FirstRun({ session, onOpened, onAlreadySetUp }: FirstRunProps): React.JSX.Element {
  const [installed] = useState(isInstalledApp)
  const [step, setStep] = useState<Step>(installed ? 'language' : 'install')
  const [code, setCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // The chosen lock, kept only until the keyring is written.
  const secret = useRef('')
  // Restore path: the picked file and its passphrase, then the opened backup,
  // kept only until it is written (or this screen goes away).
  const [backupFile, setBackupFile] = useState<File | null>(null)
  const [backupPassphrase, setBackupPassphrase] = useState('')
  const restored = useRef<OpenedBackup | null>(null)

  useEffect(() => {
    if (step === 'lock' || step === 'restore-file') {
      session.preload()
    }
  }, [step, session])

  const go = (next: Step): void => {
    setProblem(null)
    setStep(next)
  }

  const finish = async (recoveryCode: string | null): Promise<void> => {
    setBusy(true)
    setProblem(null)
    await nextFrame()
    try {
      const backup = restored.current
      const result = backup
        ? await session.restoreBackup(backup, secret.current, recoveryCode)
        : await session.setUp(secret.current, recoveryCode)
      switch (result.kind) {
        case 'opened':
          secret.current = ''
          restored.current = null
          onOpened(result.store)
          break
        case 'already-set-up':
          secret.current = ''
          restored.current = null
          onAlreadySetUp()
          break
        case 'not-empty':
          setBusy(false)
          setProblem(restore.notEmpty)
          break
        case 'storage-full':
          setBusy(false)
          setProblem(restore.full)
          break
      }
    } catch {
      setBusy(false)
      setProblem(common.tryAgainLater)
    }
  }

  const openBackup = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (busy || backupFile === null || backupPassphrase.length === 0) {
      return
    }
    setBusy(true)
    setProblem(null)
    await nextFrame()
    try {
      const bytes = new Uint8Array(await backupFile.arrayBuffer())
      const result = await session.openBackupFile(bytes, backupPassphrase)
      setBusy(false)
      if (result.kind === 'opened') {
        restored.current = result.backup
        setBackupPassphrase('')
        go('restore-lock')
        return
      }
      if (result.reason === 'cannot-open') {
        setBackupPassphrase('')
      }
      setProblem(`${restore.refusals[result.reason]} ${restore.nothingChanged}`)
    } catch {
      setBusy(false)
      setProblem(common.tryAgainLater)
    }
  }

  const leaveRestore = (): void => {
    restored.current = null
    secret.current = ''
    setBackupFile(null)
    setBackupPassphrase('')
    go('privacy')
  }

  let body: React.JSX.Element
  switch (step) {
    case 'install':
      body = (
        <div className="screen__body">
          <ScreenTitle>{installGuard.title}</ScreenTitle>
          <p className="screen__text">{installGuard.why}</p>
          <p className="screen__text">{installGuard.androidSteps}</p>
          <p className="screen__text">{installGuard.iphoneSteps}</p>
          <p className="notice">{installGuard.iphoneStorage}</p>
          <div className="actions">
            <button type="button" className="button button--quiet" onClick={() => go('look-around')}>
              {installGuard.lookAround}
            </button>
          </div>
        </div>
      )
      break
    case 'look-around':
      body = (
        <div className="screen__body">
          <ScreenTitle>{installGuard.lookAroundTitle}</ScreenTitle>
          <p className="screen__text">{installGuard.lookAroundText}</p>
          <div className="actions">
            <button type="button" className="button button--quiet" onClick={() => go('install')}>
              {common.back}
            </button>
          </div>
        </div>
      )
      break
    case 'language':
      body = (
        <div className="screen__body">
          <ScreenTitle>{language.title}</ScreenTitle>
          <p className="screen__text">{language.text}</p>
          <div className="actions">
            <button type="button" className="button button--primary" onClick={() => go('intended-use')}>
              {common.continue}
            </button>
            {installed ? null : (
              <button type="button" className="button button--quiet" onClick={() => go('install')}>
                {common.back}
              </button>
            )}
          </div>
        </div>
      )
      break
    case 'intended-use':
      body = (
        <div className="screen__body">
          <ScreenTitle>{intendedUse.title}</ScreenTitle>
          <p className="screen__text">{intendedUse.summary}</p>
          <details className="more">
            <summary className="more__summary">{intendedUse.readAll}</summary>
            {intendedUse.details.map((line) => (
              <p key={line} className="screen__text">
                {line}
              </p>
            ))}
          </details>
          <p className="screen__text screen__text--strong">{intendedUse.notEmergency}</p>
          <p className="screen__text">{intendedUse.adultsOnly}</p>
          <div className="actions">
            <button type="button" className="button button--primary" onClick={() => go('privacy')}>
              {intendedUse.confirmAdult}
            </button>
            <button type="button" className="button button--quiet" onClick={() => go('language')}>
              {common.back}
            </button>
          </div>
        </div>
      )
      break
    case 'privacy':
      body = (
        <div className="screen__body">
          <ScreenTitle>{privacy.title}</ScreenTitle>
          <ul className="screen__list">
            {privacy.points.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="actions">
            <button type="button" className="button button--primary" onClick={() => go('lock')}>
              {common.continue}
            </button>
            <button type="button" className="button button--quiet" onClick={() => go('intended-use')}>
              {common.back}
            </button>
            <button type="button" className="button button--quiet" onClick={() => go('restore-file')}>
              {restore.entry}
            </button>
          </div>
        </div>
      )
      break
    case 'restore-file':
      body = (
        <form className="screen__body" onSubmit={(event) => void openBackup(event)} noValidate>
          <ScreenTitle>{restore.title}</ScreenTitle>
          <p className="screen__text">{restore.text}</p>
          <div className="field">
            <label className="field__label" htmlFor="backup-file">
              {restore.file}
            </label>
            {/* No `accept` filter: Android cannot map the `.feelbackup` extension
                to a type and may grey the file out. The file is checked anyway. */}
            <input
              id="backup-file"
              className="field__file"
              type="file"
              disabled={busy}
              onChange={(event) => {
                setProblem(null)
                setBackupFile(event.target.files?.[0] ?? null)
              }}
            />
          </div>
          <SecretField
            id="backup-passphrase"
            label={restore.passphrase}
            value={backupPassphrase}
            onChange={setBackupPassphrase}
            disabled={busy}
          />
          {problem ? (
            <p className="screen__problem" role="alert">
              {problem}
            </p>
          ) : null}
          {busy ? (
            <p className="screen__status" role="status">
              {common.opening}
            </p>
          ) : null}
          <div className="actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={busy || backupFile === null || backupPassphrase.length === 0}
            >
              {restore.open}
            </button>
            <button type="button" className="button button--quiet" disabled={busy} onClick={leaveRestore}>
              {common.back}
            </button>
          </div>
        </form>
      )
      break
    case 'restore-lock':
      body = (
        <SetLock
          title={restore.lockTitle}
          intro={restore.lockText}
          submitLabel={common.continue}
          busy={false}
          message={null}
          onChosen={(chosen) => {
            secret.current = chosen
            go('recovery')
          }}
          onBack={leaveRestore}
        />
      )
      break
    case 'lock':
      body = (
        <SetLock
          title={setLock.title}
          submitLabel={common.continue}
          busy={false}
          message={null}
          onChosen={(chosen) => {
            secret.current = chosen
            go('recovery')
          }}
          onBack={() => go('privacy')}
        />
      )
      break
    case 'recovery':
      body = (
        <div className="screen__body">
          <ScreenTitle>{recovery.title}</ScreenTitle>
          <p className="screen__text">{recovery.text}</p>
          {code === null ? null : (
            <div className="recovery">
              <p className="recovery__label">{recovery.codeLabel}</p>
              <p className="recovery__code">{code}</p>
            </div>
          )}
          {restored.current === null ? <p className="screen__text">{recovery.backup}</p> : null}
          {problem ? (
            <p className="screen__problem" role="alert">
              {problem}
            </p>
          ) : null}
          {busy ? (
            <p className="screen__status" role="status">
              {common.opening}
            </p>
          ) : null}
          <div className="actions">
            {code === null ? (
              <>
                <button type="button" className="button button--primary" disabled={busy} onClick={() => setCode(newRecoveryCode())}>
                  {recovery.make}
                </button>
                <button type="button" className="button button--quiet" disabled={busy} onClick={() => void finish(null)}>
                  {recovery.skip}
                </button>
              </>
            ) : (
              <button type="button" className="button button--primary" disabled={busy} onClick={() => void finish(code)}>
                {recovery.saved}
              </button>
            )}
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => {
                setCode(null)
                secret.current = ''
                go(restored.current === null ? 'lock' : 'restore-lock')
              }}
            >
              {common.back}
            </button>
          </div>
        </div>
      )
      break
  }

  return (
    <section key={step} className="screen">
      {body}
    </section>
  )
}
